import React, { useCallback, useEffect, useState } from "react";
import { getQualityControls, getQualitySites, deleteQualityControl, sendQualityReminders, getQualityReminderStatus } from "../../api";
import ActionsList from "../quality/ActionsList";
import { useToast } from "../../hooks/use-toast";
import { Plus, ShieldCheck, AlertTriangle, TrendingUp, CalendarCheck, Trash2, X, Search, BellRing, Loader2 } from "lucide-react";
import QualityForm from "../quality/QualityForm";
import QualityDetail, { LevelBadge, fmtDate } from "../quality/QualityDetail";
import { log } from "../../lib/log";

const Stat = ({ icon: Icon, label, value, tone = "text-neutral-900", testid }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4"><p className="text-xs text-neutral-500 flex items-center gap-1.5"><Icon size={13} /> {label}</p><p className={`text-2xl font-bold mt-1 ${tone}`} data-testid={testid}>{value}</p></div>
);

const SitesToControl = ({ sites, onNew }) => {
  const late = sites.filter((s) => s.late);
  if (!late.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="quality-late-sites">
      <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-2"><AlertTriangle size={15} /> {late.length} site(s) à contrôler (contrôle jamais réalisé ou en retard)</p>
      <div className="flex flex-wrap gap-2">
        {late.slice(0, 12).map((s) => (
          <button key={s.client_nom} onClick={() => onNew(s)} className="text-xs bg-white border border-amber-200 hover:border-brand rounded-full px-3 py-1.5 text-neutral-800" data-testid={`quality-late-${s.client_nom}`}>
            {s.client_nom}{s.last_control ? <span className="text-neutral-500"> · dernier {fmtDate(s.last_control)}</span> : <span className="text-neutral-500"> · jamais contrôlé</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

const QualityPanel = ({ isMaster }) => {
  const [data, setData] = useState({ items: [], stats: {} });
  const [sites, setSites] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(null);
  const [selected, setSelected] = useState(null);
  const [remind, setRemind] = useState({ status: null, busy: false });
  const { toast } = useToast();
  const loadRemind = useCallback(() => getQualityReminderStatus().then((r) => setRemind((x) => ({ ...x, status: r.data }))).catch(log.error), []);
  useEffect(() => { loadRemind(); }, [loadRemind]);
  const sendReminders = async () => {
    setRemind((x) => ({ ...x, busy: true }));
    try { const { data } = await sendQualityReminders(); toast({ title: "Rappels envoyés ✓", description: `${data.reminder.late_sites} site(s) en retard · ${data.overdue.alerted} action(s) alertée(s)` }); loadRemind(); }
    catch (e) { toast({ title: "Envoi impossible", description: e?.response?.data?.detail || "Erreur", variant: "destructive" }); }
    finally { setRemind((x) => ({ ...x, busy: false })); }
  };

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([getQualityControls(q ? { client: q } : {}), getQualitySites()]);
      setData(c.data); setSites(s.data.sites);
    } catch (e) { log.error(e); toast({ title: "Chargement impossible", variant: "destructive" }); }
  }, [q, toast]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce contrôle ?")) return;
    await deleteQualityControl(id); setSelected(null); load();
  };
  const st = data.stats;

  return (
    <div className="space-y-5" data-testid="quality-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-lg font-bold text-neutral-900 flex items-center gap-2"><ShieldCheck size={20} className="text-brand" /> Contrôle qualité</h2><p className="text-sm text-neutral-500">Contrôles réalisés par les majors sur site, suivi de la conformité et rapports clients.</p></div>
        <div className="flex items-center gap-2">
          {isMaster && <button onClick={sendReminders} disabled={remind.busy} className="border border-neutral-300 hover:border-brand text-neutral-700 font-semibold px-3 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60" title="Rappel des sites en retard + alertes d'échéances" data-testid="quality-send-reminders">{remind.busy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Envoyer les rappels</button>}
          <button onClick={() => setCreating({})} className="bg-brand hover:bg-brand-dark text-white font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2" data-testid="quality-new"><Plus size={15} /> Nouveau contrôle</button>
        </div>
      </div>
      {remind.status && <p className="text-xs text-neutral-500 -mt-2" data-testid="quality-reminder-status">{remind.status.schedule}{remind.status.reminder?.at && ` · dernier rappel : ${new Date(remind.status.reminder.at).toLocaleString("fr-FR")} (${remind.status.reminder.late_sites} site(s))`}{remind.status.overdue?.at && ` · dernière alerte échéances : ${new Date(remind.status.overdue.at).toLocaleString("fr-FR")}`}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={CalendarCheck} label="Contrôles ce mois" value={st.this_month ?? 0} testid="quality-stat-month" />
        <Stat icon={TrendingUp} label="Conformité moyenne" value={st.avg_score != null ? `${st.avg_score} %` : "—"} tone={st.avg_score >= 85 ? "text-emerald-600" : "text-amber-600"} testid="quality-stat-avg" />
        <Stat icon={AlertTriangle} label="Non conformes" value={st.non_conformes ?? 0} tone={st.non_conformes ? "text-red-600" : "text-neutral-900"} testid="quality-stat-nc" />
        <Stat icon={ShieldCheck} label="Sites à contrôler" value={sites.filter((s) => s.late).length} tone="text-amber-600" testid="quality-stat-late" />
      </div>

      <SitesToControl sites={sites} onNew={(s) => setCreating(s)} />

      <ActionsList onChanged={load} />

      <div className="relative w-72"><Search size={13} className="absolute left-2.5 top-2.5 text-neutral-400" /><input className="w-full border border-neutral-300 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-brand" placeholder="Filtrer par client…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quality-search" /></div>

      {data.items.length === 0 ? <p className="text-sm text-neutral-500 text-center py-10" data-testid="quality-empty">Aucun contrôle qualité enregistré.</p> : (
        <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100" data-testid="quality-list">
          {data.items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 cursor-pointer" onClick={() => setSelected(c)} data-testid={`quality-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-neutral-900 truncate">{c.client_nom}</p>
                <p className="text-xs text-neutral-500">{fmtDate(c.date)} · {c.major_nom} · {c.photos?.length || 0} photo(s){c.signature ? " · visa client" : ""}{c.report_sent_at ? " · rapport envoyé" : ""}</p>
              </div>
              <LevelBadge qc={c} />
              {isMaster && <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="text-neutral-400 hover:text-red-500 p-1" data-testid={`quality-delete-${c.id}`}><Trash2 size={15} /></button>}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setCreating(null)} data-testid="quality-form-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-6 shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h3 className="font-heading font-bold text-lg">Nouveau contrôle qualité</h3><button onClick={() => setCreating(null)} className="text-neutral-400 hover:text-neutral-700" data-testid="quality-form-close"><X size={18} /></button></div>
            <QualityForm initialSite={creating.client_nom ? creating : null} onCancel={() => setCreating(null)} onDone={(qc) => { setCreating(null); load(); toast({ title: `Contrôle enregistré — ${qc.score_pct} %`, description: qc.report_sent_at ? "Rapport envoyé au client." : qc.report_error ? `Rapport non envoyé : ${qc.report_error}` : undefined }); }} />
          </div>
        </div>
      )}
      {selected && <QualityDetail qc={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
};

export default QualityPanel;
